/**
 * V57: 조직원 평가 페이지
 *
 * - 조직장(role_manager)이 소속 조직원의 평가를 관리
 * - 평가 생성, 리드평가, 팀장 최종평가, HR 발송
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
  FINAL_GRADE_LABELS,
  EvaluationItem,
  EvaluationSection,
  EvaluationStatus,
  EvaluationType,
  FinalGrade,
  EvaluationTemplateItem,
  EvaluationNode,
  EvaluationItemV2,
  ScoringScale,
  EvaluationTotals,
} from '../../shared/hooks/useEvaluations';
import { ProbationEvaluationForm, isProbationType } from '../../shared/components/ProbationEvaluationForm';
// V60: 탭 구조 + ProbationEvaluationTab 렌더링
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { ProbationEvaluationTab } from './components/ProbationEvaluationTab';
import { WorkEvaluationTab } from './components/WorkEvaluationTab';
import { EmployeeEvaluationHistoryPanel } from './components/EmployeeEvaluationHistoryPanel';
import { useOrgEvaluations } from '../../shared/hooks/useEvaluations';

// ============================================
// 평가 생성 모달
// ============================================

interface CreateEvaluationModalProps {
  templates: EvaluationTemplateItem[];
  onClose: () => void;
  onCreate: (params: {
    targetUserId: string;
    templateId: string;
    evaluationYear: number;
    evaluationPeriod?: string;
    leadEvaluatorId?: string;
  }) => Promise<void>;
}

function CreateEvaluationModal({ templates, onClose, onCreate }: CreateEvaluationModalProps) {
  const [targetUserId, setTargetUserId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [evaluationYear, setEvaluationYear] = useState(new Date().getFullYear());
  const [evaluationPeriod, setEvaluationPeriod] = useState('');
  const [leadEvaluatorId, setLeadEvaluatorId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!targetUserId || !templateId) return;
    setSubmitting(true);
    try {
      await onCreate({
        targetUserId,
        templateId,
        evaluationYear,
        evaluationPeriod: evaluationPeriod || undefined,
        leadEvaluatorId: leadEvaluatorId || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500,
      }}>
        <h3 style={{ marginTop: 0 }}>평가 생성</h3>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>대상 직원 ID *</label>
          <input
            type="text"
            value={targetUserId}
            onChange={e => setTargetUserId(e.target.value)}
            placeholder="직원 UID 입력"
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>평가 양식 *</label>
          <select
            value={templateId}
            onChange={e => setTemplateId(e.target.value)}
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
          >
            <option value="">양식 선택</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>
                {t.title} ({EVALUATION_TYPE_LABELS[t.type]}) v{t.version}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>평가 연도</label>
          <input
            type="number"
            value={evaluationYear}
            onChange={e => setEvaluationYear(Number(e.target.value))}
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>평가 기간 (선택)</label>
          <input
            type="text"
            value={evaluationPeriod}
            onChange={e => setEvaluationPeriod(e.target.value)}
            placeholder="예: 2026-01 ~ 2026-03"
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>리드 평가자 ID (선택)</label>
          <input
            type="text"
            value={leadEvaluatorId}
            onChange={e => setLeadEvaluatorId(e.target.value)}
            placeholder="리드 평가자 UID (없으면 생략)"
            style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: 'white', cursor: 'pointer' }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !targetUserId || !templateId}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 4,
              background: (!targetUserId || !templateId) ? '#ccc' : '#3B82F6',
              color: 'white', cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '생성 중...' : '생성'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 평가 상세 패널 (팀장용)
// ============================================

interface EvaluationDetailPanelProps {
  evaluation: EvaluationItem;
  onClose: () => void;
  onSubmitManagerEval: (params: {
    evaluationId: string;
    managerEvaluation: Record<string, any>;
    managerComment?: string;
    finalGrade?: FinalGrade;
    finalGradeDetail?: string;
    finalComment?: string;
  }) => Promise<boolean>;
  onSendToHr: (evaluationId: string) => Promise<boolean>;
  onRefresh: () => void;
  submitError?: string | null;
}

// V2 helpers
function getV2TopNodes(nodes: EvaluationNode[]): EvaluationNode[] {
  return nodes.filter(n => n.parentId === null && n.isActive).sort((a, b) => a.order - b.order);
}
function getV2ChildNodes(nodes: EvaluationNode[], parentId: string): EvaluationNode[] {
  return nodes.filter(n => n.parentId === parentId && n.isActive).sort((a, b) => a.order - b.order);
}
function getV2NodeItems(items: EvaluationItemV2[], nodeId: string, role: string): EvaluationItemV2[] {
  return items.filter(i => i.nodeId === nodeId && i.isActive && i.requiredFor.includes(role as any)).sort((a, b) => a.order - b.order);
}

function V2TotalsDisplay({ totals, label, bgColor }: { totals: EvaluationTotals; label: string; bgColor?: string }) {
  const categories = Object.values(totals.byCategory);
  return (
    <div style={{ background: bgColor || '#F0FDF4', padding: 12, borderRadius: 4, marginBottom: 8 }}>
      <strong style={{ fontSize: 13 }}>{label}</strong>
      {categories.map(cat => (
        <div key={cat.nodeId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
          <span>{cat.label}</span><span>{Number(cat.total).toFixed(2)} / {Number(cat.maxPossible).toFixed(2)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: 6, paddingTop: 4, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
        <span>합계</span><span>{Number(totals.grandTotal).toFixed(2)} / {Number(totals.grandMaxPossible).toFixed(2)}</span>
      </div>
    </div>
  );
}

function V2ReadOnlyScores({ evalData, items, nodes, role, scale }: {
  evalData: Record<string, any>; items: EvaluationItemV2[]; nodes: EvaluationNode[]; role: string; scale: ScoringScale;
}) {
  const topNodes = getV2TopNodes(nodes);
  const renderItems = (nodeId: string, depth: number): React.ReactNode => {
    const nodeItems = getV2NodeItems(items, nodeId, role);
    const children = getV2ChildNodes(nodes, nodeId);
    return (
      <>
        {nodeItems.map(item => (
          <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2, paddingLeft: depth * 12 }}>
            <span>{item.title}</span>
            <span style={{ fontWeight: 500 }}>{evalData[item.id] ?? '-'}</span>
          </div>
        ))}
        {children.map(child => (
          <div key={child.id}>
            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginTop: 4, paddingLeft: depth * 12 }}>{child.title}</div>
            {renderItems(child.id, depth + 1)}
          </div>
        ))}
      </>
    );
  };
  return <>{topNodes.map(n => (
    <div key={n.id} style={{ marginBottom: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{n.title}</div>
      {renderItems(n.id, 0)}
    </div>
  ))}</>;
}

function V2ManagerForm({ items, nodes, scale, formData, onChange }: {
  items: EvaluationItemV2[]; nodes: EvaluationNode[]; scale: ScoringScale;
  formData: Record<string, any>; onChange: (id: string, v: any) => void;
}) {
  const topNodes = getV2TopNodes(nodes);
  const renderNodeItems = (nodeId: string, depth: number): React.ReactNode => {
    const nodeItems = getV2NodeItems(items, nodeId, 'manager');
    const children = getV2ChildNodes(nodes, nodeId);
    return (
      <>
        {nodeItems.map(item => (
          <div key={item.id} style={{ marginBottom: 10, paddingLeft: depth * 12 }}>
            <label style={{ fontWeight: 500, display: 'block', marginBottom: 4, fontSize: 13 }}>
              {item.title}
              {item.inputType === 'rating' && item.weight !== 1 && (
                <span style={{ color: '#999', fontSize: 11, marginLeft: 4 }}>x{item.weight}</span>
              )}
            </label>
            {item.inputType === 'text' ? (
              <textarea value={formData[item.id] || ''} onChange={e => onChange(item.id, e.target.value)} rows={2}
                style={{ width: '100%', padding: 6, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical', fontSize: 13 }} />
            ) : item.inputType === 'select' ? (
              <select value={formData[item.id] || ''} onChange={e => onChange(item.id, e.target.value)}
                style={{ width: '100%', padding: 6, border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}>
                <option value="">선택</option>
                {(item.options || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Array.from({ length: Math.floor((scale.max - scale.min) / scale.step) + 1 }, (_, i) => scale.min + i * scale.step).map(n => (
                  <button key={n} onClick={() => onChange(item.id, n)} style={{
                    width: 30, height: 30, borderRadius: 4, border: '1px solid #ddd', fontSize: 12,
                    background: formData[item.id] === n ? '#3B82F6' : 'white', color: formData[item.id] === n ? 'white' : '#333',
                    cursor: 'pointer', fontWeight: 600,
                  }}>{n}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        {children.map(child => (
          <div key={child.id}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#4B5563', marginTop: 6, paddingLeft: depth * 12 }}>{child.title}</div>
            {renderNodeItems(child.id, depth + 1)}
          </div>
        ))}
      </>
    );
  };
  return <>{topNodes.map(n => (
    <div key={n.id} style={{ marginBottom: 12, padding: 8, background: '#F9FAFB', borderRadius: 6 }}>
      <h5 style={{ margin: '0 0 6px', fontSize: 14 }}>{n.title}</h5>
      {renderNodeItems(n.id, 0)}
    </div>
  ))}</>;
}

function EvaluationDetailPanel({ evaluation, onClose, onSubmitManagerEval, onSendToHr, onRefresh, submitError }: EvaluationDetailPanelProps) {
  const isV2 = (evaluation.templateNodes?.length || 0) > 0;
  const sections = evaluation.templateSections || [];
  const managerSections = sections.filter(s => s.requiredFor.includes('manager'));

  const [formData, setFormData] = useState<Record<string, any>>(evaluation.managerEvaluation || {});
  const [comment, setComment] = useState(evaluation.managerComment || '');
  const [finalGrade, setFinalGrade] = useState<FinalGrade | ''>(evaluation.finalGrade || '');
  const [finalComment, setFinalComment] = useState(evaluation.finalComment || '');
  const [submitting, setSubmitting] = useState(false);

  // V63(A5): manager_submitted 재제출 허용
  const canSubmitManager =
    evaluation.status === 'self_submitted' ||
    evaluation.status === 'lead_submitted' ||
    evaluation.status === 'manager_submitted';
  const canSendToHr = evaluation.status === 'manager_submitted';

  const handleFieldChange = (id: string, value: any) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  // V63: probation_3m은 _i16에서 finalGrade/finalGradeDetail 파생
  const isProbation3m = evaluation.templateType === 'probation_3m';

  const handleManagerSubmit = async () => {
    if (!isProbation3m && !finalGrade) return;
    setSubmitting(true);

    // V63: probation_3m — _i16에서 등급 파생
    let submitGrade = finalGrade as FinalGrade | undefined;
    let submitGradeDetail: string | undefined;
    if (isProbation3m) {
      const i16Key = Object.keys(formData).find(k => k.endsWith('_i16'));
      const i16Value = i16Key ? formData[i16Key] : null;
      if (i16Value && typeof i16Value === 'string') {
        submitGradeDetail = i16Value;
        const gradeChar = i16Value.charAt(0).toUpperCase();
        if (['A', 'B', 'C', 'D'].includes(gradeChar)) {
          submitGrade = gradeChar as FinalGrade;
        }
      }
    }

    const success = await onSubmitManagerEval({
      evaluationId: evaluation.id,
      managerEvaluation: formData,
      managerComment: comment || undefined,
      finalGrade: submitGrade,
      finalGradeDetail: submitGradeDetail,
      finalComment: finalComment || undefined,
    });
    setSubmitting(false);
    if (success) onRefresh();
  };

  const handleSendToHr = async () => {
    setSubmitting(true);
    const success = await onSendToHr(evaluation.id);
    setSubmitting(false);
    if (success) onRefresh();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: '60%', maxWidth: 900, minWidth: 400,
      background: 'white', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', padding: 24, overflow: 'auto', zIndex: 1000,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>평가 상세</h3>
        <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer' }}>x</button>
      </div>

      {/* 기본 정보 */}
      <div style={{ background: '#F9FAFB', padding: 16, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 14 }}>
          <div><span style={{ color: '#666' }}>대상자:</span> {evaluation.targetUserName}</div>
          <div><span style={{ color: '#666' }}>소속:</span> {evaluation.targetUserOrgUnitName}</div>
          <div><span style={{ color: '#666' }}>유형:</span> {EVALUATION_TYPE_LABELS[evaluation.templateType]}</div>
          <div><span style={{ color: '#666' }}>상태:</span> {EVALUATION_STATUS_LABELS[evaluation.status]}</div>
          {evaluation.evaluationPeriod && (
            <div><span style={{ color: '#666' }}>기간:</span> {evaluation.evaluationPeriod}</div>
          )}
          {/* V66: 멘토 → 평가자 순서 (멘토는 leadEvaluatorName 있을 때만 표시). */}
          {evaluation.leadEvaluatorName && (
            <div><span style={{ color: '#666' }}>멘토:</span> {evaluation.leadEvaluatorName}</div>
          )}
          {evaluation.evaluatorName && (
            <div><span style={{ color: '#666' }}>평가자:</span> {evaluation.evaluatorName}</div>
          )}
        </div>
      </div>

      {/* 자가평가 결과 */}
      {evaluation.selfEvaluation && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px' }}>자가평가 결과</h4>
          {isV2 && evaluation.selfTotals ? (
            <V2TotalsDisplay totals={evaluation.selfTotals} label="자가평가 점수" bgColor="#F0FDF4" />
          ) : (
            <div style={{ background: '#F0FDF4', padding: 12, borderRadius: 4, fontSize: 14 }}>
              {sections.filter(s => s.requiredFor.includes('self')).map(section => (
                <div key={section.id} style={{ marginBottom: 4 }}>
                  <strong>{section.title}:</strong> {evaluation.selfEvaluation?.[section.id] ?? '-'}
                </div>
              ))}
            </div>
          )}
          {evaluation.selfComment && <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 13 }}>총평: {evaluation.selfComment}</div>}
        </div>
      )}

      {/* 리드평가 결과 (수습평가에는 리드평가 없음 — 기존 유형만) */}
      {evaluation.leadEvaluation && !isProbationType(evaluation.templateType) && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px' }}>리드평가 결과</h4>
          {isV2 && evaluation.leadTotals ? (
            <V2TotalsDisplay totals={evaluation.leadTotals} label="리드평가 점수" bgColor="#EFF6FF" />
          ) : (
            <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 4, fontSize: 14 }}>
              {sections.filter(s => s.requiredFor.includes('lead')).map(section => (
                <div key={section.id} style={{ marginBottom: 4 }}>
                  <strong>{section.title}:</strong> {evaluation.leadEvaluation?.[section.id] ?? '-'}
                </div>
              ))}
            </div>
          )}
          {/* V66: 3차 평가(probation_3m)에서는 "멘토 총평" 미노출. 1차/2차 그대로. */}
          {evaluation.leadComment && evaluation.templateType !== 'probation_3m' && (
            <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 13 }}>총평: {evaluation.leadComment}</div>
          )}
        </div>
      )}

      {/* 팀장 최종평가 입력 */}
      {canSubmitManager && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px' }}>팀장 최종평가</h4>

          {/* V57.8: 수습사원 평가 → PPT 테이블 폼 */}
          {isV2 && isProbationType(evaluation.templateType) ? (
            <ProbationEvaluationForm
              mode="manager"
              nodes={evaluation.templateNodes || []}
              items={evaluation.templateItems || []}
              scoringScale={evaluation.templateScoringScale || { min: 1, max: 4, step: 1 }}
              templateType={evaluation.templateType}
              selfEvaluation={evaluation.selfEvaluation || undefined}
              leadEvaluation={evaluation.leadEvaluation || undefined}
              formData={formData}
              onChange={handleFieldChange}
              attendanceSummary={evaluation.attendanceSummary}
            />
          ) : isV2 ? (
            <V2ManagerForm
              items={evaluation.templateItems || []}
              nodes={evaluation.templateNodes || []}
              scale={evaluation.templateScoringScale || { min: 1, max: 5, step: 1 }}
              formData={formData}
              onChange={handleFieldChange}
            />
          ) : (
            managerSections.map(section => (
              <div key={section.id} style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>
                  {section.title}
                </label>
                {section.inputType === 'text' ? (
                  <textarea
                    value={formData[section.id] || ''}
                    onChange={e => handleFieldChange(section.id, e.target.value)}
                    rows={2}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
                  />
                ) : section.inputType === 'select' ? (
                  <select
                    value={formData[section.id] || ''}
                    onChange={e => handleFieldChange(section.id, e.target.value)}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
                  >
                    <option value="">선택</option>
                    {(section.options || []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Array.from({ length: section.inputType === 'rating_5' ? 5 : 10 }, (_, i) => i + 1).map(n => (
                      <button
                        key={n}
                        onClick={() => handleFieldChange(section.id, n)}
                        style={{
                          width: 32, height: 32, borderRadius: 4, border: '1px solid #ddd',
                          background: formData[section.id] === n ? '#3B82F6' : 'white',
                          color: formData[section.id] === n ? 'white' : '#333',
                          cursor: 'pointer', fontWeight: 600, fontSize: 13,
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>총평 (선택)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
            />
          </div>

          {/* V63: probation_3m은 템플릿 _i16/_i14로 등급/의견 통합, 별도 입력 숨김 */}
          {!isProbation3m && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>최종 등급 *</label>
                <select
                  value={finalGrade}
                  onChange={e => setFinalGrade(e.target.value as FinalGrade)}
                  style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
                >
                  <option value="">등급 선택</option>
                  {(['S', 'A', 'B', 'C', 'D'] as FinalGrade[]).map(g => (
                    <option key={g} value={g}>{FINAL_GRADE_LABELS[g]}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: 14 }}>최종 코멘트 (선택)</label>
                <textarea
                  value={finalComment}
                  onChange={e => setFinalComment(e.target.value)}
                  rows={2}
                  style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
                />
              </div>
            </>
          )}

          {/* V57.3+: 검증 에러 표시 */}
          {submitError && (
            <div style={{ padding: 10, background: '#FEF2F2', color: '#DC2626', borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
              {submitError}
            </div>
          )}

          <button
            onClick={handleManagerSubmit}
            disabled={submitting || (!isProbation3m && !finalGrade)}
            style={{
              padding: '10px 20px', border: 'none', borderRadius: 4, width: '100%',
              background: (!isProbation3m && !finalGrade) ? '#ccc' : '#3B82F6', color: 'white',
              cursor: submitting || (!isProbation3m && !finalGrade) ? 'not-allowed' : 'pointer', fontWeight: 600,
            }}
          >
            {submitting ? '제출 중...' : evaluation.status === 'manager_submitted' ? '최종평가 재제출' : '최종평가 제출'}
          </button>
        </div>
      )}

      {/* 팀장 최종평가 완료 결과 표시 */}
      {(evaluation.status === 'manager_submitted' || evaluation.status === 'sent_to_hr') && evaluation.finalGrade && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px' }}>최종평가 결과</h4>
          {/* V57.8: 수습사원 평가 → PPT 테이블 폼 (readonly) */}
          {isV2 && isProbationType(evaluation.templateType) ? (
            <ProbationEvaluationForm
              mode="readonly"
              nodes={evaluation.templateNodes || []}
              items={evaluation.templateItems || []}
              scoringScale={evaluation.templateScoringScale || { min: 1, max: 4, step: 1 }}
              templateType={evaluation.templateType}
              selfEvaluation={evaluation.selfEvaluation || undefined}
              leadEvaluation={evaluation.leadEvaluation || undefined}
              managerEvaluation={evaluation.managerEvaluation || undefined}
              formData={{}}
              selfTotals={evaluation.selfTotals}
              managerTotals={evaluation.managerTotals}
              attendanceSummary={evaluation.attendanceSummary}
            />
          ) : isV2 && evaluation.managerTotals ? (
            <V2TotalsDisplay totals={evaluation.managerTotals} label="팀장평가 점수" bgColor="#FEF3C7" />
          ) : null}
          <div style={{ background: '#FEF3C7', padding: 12, borderRadius: 4, fontSize: 14 }}>
            <div><strong>등급:</strong> {FINAL_GRADE_LABELS[evaluation.finalGrade]}</div>
            {evaluation.managerComment && <div style={{ marginTop: 4 }}><strong>총평:</strong> {evaluation.managerComment}</div>}
            {evaluation.finalComment && <div style={{ marginTop: 4 }}><strong>코멘트:</strong> {evaluation.finalComment}</div>}
          </div>
        </div>
      )}

      {/* HR 발송 */}
      {canSendToHr && (
        <button
          onClick={handleSendToHr}
          disabled={submitting}
          style={{
            padding: '10px 20px', border: 'none', borderRadius: 4, width: '100%',
            background: '#059669', color: 'white',
            cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600,
          }}
        >
          {submitting ? '발송 중...' : '인사팀 발송'}
        </button>
      )}

      {evaluation.status === 'sent_to_hr' && (
        <div style={{ padding: 12, background: '#D1FAE5', borderRadius: 4, textAlign: 'center', color: '#065F46', fontWeight: 600, marginTop: 12 }}>
          인사팀 승인 대기
        </div>
      )}
    </div>
  );
}

// ============================================
// V307: UpperConfirmTab — 상위 조직장 컨펌 요청 탭
// ============================================

function UpperConfirmTab({
  evaluations,
  loading,
  onRefresh,
  onEmployeeNameClick,
}: {
  evaluations: EvaluationItem[];
  loading: boolean;
  onRefresh: () => void;
  onEmployeeNameClick?: (employeeId: string, employeeName: string) => void;
}) {
  const {
    getEvaluationDetail,
    approveWorkEvaluation,
  } = useOrgEvaluations();

  const [detailEval, setDetailEval] = useState<EvaluationItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleViewDetail = useCallback(async (evalItem: EvaluationItem) => {
    const detail = await getEvaluationDetail(evalItem.id);
    if (detail) setDetailEval(detail);
  }, [getEvaluationDetail]);

  const handleUpperDecision = useCallback(async (evalItem: EvaluationItem, decision: 'approved' | 'rejected') => {
    const confirmMsg = decision === 'approved' ? '승인하시겠습니까?' : '반려하시겠습니까?';
    if (!window.confirm(confirmMsg)) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ok = await approveWorkEvaluation({ evaluationId: evalItem.id, decision });
      if (ok) {
        setDetailEval(null);
        onRefresh();
      }
    } catch (err: any) {
      setSubmitError(err?.message || '처리 실패');
    } finally {
      setSubmitting(false);
    }
  }, [approveWorkEvaluation, onRefresh]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>로딩 중...</div>;
  }

  if (evaluations.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-500)', background: 'var(--color-gray-50)', borderRadius: 8 }}>
        <p style={{ fontSize: 14 }}>컨펌 대기 중인 평가가 없습니다.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--color-gray-50)', borderBottom: '2px solid var(--color-gray-200)' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>평가 대상자</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>소속 조직</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>평가유형</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>평가연도</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>평가자</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>상태</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>액션</th>
            </tr>
          </thead>
          <tbody>
            {evaluations.map(evalItem => (
              <tr key={evalItem.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '10px 12px' }}>
                  <span
                    onClick={() => onEmployeeNameClick?.(evalItem.targetUserId, evalItem.targetUserName)}
                    style={{ cursor: 'pointer', color: 'var(--color-primary)', textDecoration: 'underline' }}
                  >
                    {evalItem.targetUserName}
                  </span>
                </td>
                <td style={{ padding: '10px 12px' }}>{evalItem.targetUserOrgUnitName || '-'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  {EVALUATION_TYPE_LABELS[evalItem.templateType as EvaluationType] || evalItem.templateType}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{evalItem.evaluationYear}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>{evalItem.evaluatorName || '-'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: '#FEF3C7', color: '#92400E',
                  }}>
                    {EVALUATION_STATUS_LABELS[evalItem.status as EvaluationStatus] || evalItem.status}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleUpperDecision(evalItem, 'approved')}
                      disabled={submitting}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        border: 'none', background: '#10B981', color: 'white', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      승인
                    </button>
                    <button
                      onClick={() => handleUpperDecision(evalItem, 'rejected')}
                      disabled={submitting}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        border: 'none', background: '#EF4444', color: 'white', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      반려
                    </button>
                    <button
                      onClick={() => handleViewDetail(evalItem)}
                      style={{
                        padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                        border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      상세
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {submitError && (
        <div style={{ marginTop: 8, padding: 8, background: '#FEE2E2', color: '#991B1B', borderRadius: 4, fontSize: 13 }}>
          {submitError}
        </div>
      )}

      {/* 상세 모달 */}
      {detailEval && (
        <UpperConfirmDetailModal
          evaluation={detailEval}
          onClose={() => setDetailEval(null)}
          onApprove={() => handleUpperDecision(detailEval, 'approved')}
          onReject={() => handleUpperDecision(detailEval, 'rejected')}
          submitting={submitting}
        />
      )}
    </div>
  );
}

// ============================================
// V307: UpperConfirmDetailModal — 컨펌 상세 모달
// ============================================

function UpperConfirmDetailModal({
  evaluation,
  onClose,
  onApprove,
  onReject,
  submitting,
}: {
  evaluation: EvaluationItem;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  submitting: boolean;
}) {
  const isProbation = isProbationType(evaluation.templateType);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 8, width: '90%', maxWidth: 700,
        maxHeight: '85vh', overflow: 'auto', padding: 24,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>평가 상세 — 컨펌 요청</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {/* 기본 정보 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 13 }}>
          <div><strong>대상자:</strong> {evaluation.targetUserName}</div>
          <div><strong>소속:</strong> {evaluation.targetUserOrgUnitName || '-'}</div>
          <div><strong>평가유형:</strong> {EVALUATION_TYPE_LABELS[evaluation.templateType as EvaluationType] || evaluation.templateType}</div>
          <div><strong>평가연도:</strong> {evaluation.evaluationYear}</div>
          {/* V66: 멘토 → 평가자 순서 (멘토는 있을 때만 표시) */}
          {evaluation.leadEvaluatorName && (
            <div><strong>멘토:</strong> {evaluation.leadEvaluatorName}</div>
          )}
          <div><strong>평가자:</strong> {evaluation.evaluatorName || '-'}</div>
          <div><strong>상태:</strong> {EVALUATION_STATUS_LABELS[evaluation.status as EvaluationStatus] || evaluation.status}</div>
        </div>

        {/* 팀장 평가 코멘트 */}
        {evaluation.managerComment && (
          <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>팀장 평가 의견</div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{evaluation.managerComment}</div>
          </div>
        )}

        {/* 팀장 점수 */}
        {evaluation.managerTotals && (
          <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>팀장 평가 점수</div>
            <div style={{ fontSize: 13 }}>총점: {evaluation.managerTotals.grandTotal ?? '-'}</div>
          </div>
        )}

        {/* 수습평가 양식 (readonly) */}
        {isProbation && evaluation.templateNodes && (
          <ProbationEvaluationForm
            nodes={evaluation.templateNodes}
            items={evaluation.templateItems || []}
            scoringScale={evaluation.templateScoringScale!}
            templateType={evaluation.templateType}
            mode="readonly"
            formData={{}}
            selfEvaluation={evaluation.selfEvaluation}
            leadEvaluation={evaluation.leadEvaluation}
            managerEvaluation={evaluation.managerEvaluation}
            managerTotals={evaluation.managerTotals}
          />
        )}

        {/* 최종등급 */}
        {evaluation.finalGrade && (
          <div style={{ marginBottom: 16, padding: 12, background: '#EFF6FF', borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>최종등급</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1D4ED8' }}>
              {FINAL_GRADE_LABELS[evaluation.finalGrade as FinalGrade] || evaluation.finalGrade}
            </div>
          </div>
        )}

        {/* 승인/반려 버튼 */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button
            onClick={onApprove}
            disabled={submitting}
            style={{
              padding: '10px 32px', borderRadius: 6, fontSize: 14, fontWeight: 600,
              border: 'none', background: '#10B981', color: 'white',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '처리 중...' : '승인'}
          </button>
          <button
            onClick={onReject}
            disabled={submitting}
            style={{
              padding: '10px 32px', borderRadius: 6, fontSize: 14, fontWeight: 600,
              border: 'none', background: '#EF4444', color: 'white',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '처리 중...' : '반려'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// OrgEvaluationsPage
// ============================================

export function OrgEvaluationsPage() {
  // V60: 3탭 구조 (V284: 컨펌요청 탭 제거)
  const [activeTab, setActiveTab] = useState<'probation' | 'work_eval' | 'task_eval'>('probation');

  // V62: 직원 평가 이력 사이드패널
  const [historyEmployee, setHistoryEmployee] = useState<{ id: string; name: string } | null>(null);
  const {
    employeeEvaluations,
    employeeEvalsLoading,
    listEmployeeEvaluations,
    getEvaluationDetail: getEvalDetailForHistory,
  } = useOrgEvaluations();

  const handleEmployeeNameClick = useCallback((id: string, name: string) => {
    setHistoryEmployee({ id, name });
  }, []);

  // 스코프 + 직원 목록 (ProbationEvaluationTab용)
  const {
    isLoading: scopeLoading,
    scopeOrgUnitIds,
    scopeMode,
  } = useOrgManagementScope();

  const isScopeReady = !scopeLoading && scopeOrgUnitIds.length > 0;

  const {
    employees,
  } = useEmployees(
    isScopeReady
      ? {
          orgUnitIds: scopeOrgUnitIds,
          scopeMode: scopeMode || undefined,
          status: 'active_or_on_leave',
        }
      : {}
  );

  // V284: 컨펌요청 탭 제거됨

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>조직원 평가</h2>
      {/* V68: 페이지 가이드 안내 */}
      <div style={{ marginBottom: '24px', color: '#94a3b8', fontSize: '13px', lineHeight: 1.7 }}>
        <p style={{ margin: 0 }}>· 수습사원 평가는 입사 후 3개월 동안 매월 진행되며, 총 3차수로 진행됩니다.</p>
        <p style={{ margin: 0 }}>· 1·2차는 자기평가 및 멘토평가가 진행되며, 3차에는 최종 조직장 평가가 진행됩니다.</p>
        <p style={{ margin: 0 }}>· 근무평가는 계약 만료가 임박한 직원 대상으로 진행됩니다.</p>
        <p style={{ margin: 0 }}>· 조직장 평가 후 상위 조직장 컨펌을 거쳐 인사팀으로 전달됩니다.</p>
      </div>

      {/* V60: 탭 바 */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 16,
        borderBottom: '2px solid var(--color-gray-200)',
      }}>
        {([
          { key: 'probation' as const, label: '수습사원평가' },
          { key: 'work_eval' as const, label: '근무평가' },
          { key: 'task_eval' as const, label: '업무평가' },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--color-primary)' : 'var(--color-gray-500)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: -2,
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 스코프 로딩 */}
      {scopeLoading && (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>로딩 중...</div>
      )}

      {/* 수습사원평가 */}
      {activeTab === 'probation' && isScopeReady && (
        <ProbationEvaluationTab employees={employees} scopeOrgUnitIds={scopeOrgUnitIds} onEmployeeNameClick={handleEmployeeNameClick} />
      )}

      {/* V63.10: 파견/계약직 근무평가 */}
      {activeTab === 'work_eval' && isScopeReady && (
        <WorkEvaluationTab employees={employees} scopeOrgUnitIds={scopeOrgUnitIds} onEmployeeNameClick={handleEmployeeNameClick} />
      )}

      {/* 업무평가 placeholder */}
      {activeTab === 'task_eval' && (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--color-gray-500)',
          background: 'var(--color-gray-50)', borderRadius: 8,
        }}>
          <p style={{ fontSize: 14 }}>업무평가 기능은 준비 중입니다.</p>
        </div>
      )}

      {/* V284: 컨펌요청 탭 제거됨 */}

      {/* V62: 직원 평가 이력 사이드패널 */}
      {historyEmployee && (
        <EmployeeEvaluationHistoryPanel
          employeeId={historyEmployee.id}
          employeeName={historyEmployee.name}
          evaluations={employeeEvaluations}
          loading={employeeEvalsLoading}
          onClose={() => setHistoryEmployee(null)}
          onLoadHistory={listEmployeeEvaluations}
          onLoadDetail={getEvalDetailForHistory}
        />
      )}
    </div>
  );
}
